import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import Header from "../components/Header";
import Footer from "../components/Footer";
import PasswordInput from "../components/PasswordInput";
import { useAuth, API } from "../context/AuthContext";
import { useSeo } from "../lib/seo";

/**
 * /account/security — the signed-in Change Password + Trusted Devices page.
 * Requires a valid session. Password change requires current + new + confirm,
 * with the option to sign out all other trusted devices at the same time.
 */
export default function AccountSecurity() {
  useSeo({ title: "Account security — BookFreely", path: "/account/security", noindex: true });
  const { user, authHeader } = useAuth();
  const nav = useNavigate();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [signOutAll, setSignOutAll] = useState(false);
  const [busy, setBusy] = useState(false);

  if (!user) {
    return (
      <div className="App">
        <Header />
        <div className="max-w-md mx-auto px-6 py-16 text-sm" style={{ color: "var(--gf-text-muted)" }}>
          Please <button className="underline" onClick={() => nav("/login?next=/account/security")}>sign in</button> to change your password.
        </div>
        <Footer />
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    if (newPassword.length < 8) return toast.error("Password must be at least 8 characters.");
    if (newPassword !== confirmPassword) return toast.error("New password and confirmation don't match.");
    if (newPassword === currentPassword) return toast.error("New password must be different from current password.");
    setBusy(true);
    try {
      const r = await axios.post(`${API}/auth/change-password`, {
        current_password: currentPassword,
        new_password: newPassword,
        sign_out_other_devices: signOutAll,
      }, { headers: authHeader() });
      toast.success(
        signOutAll && r.data?.revoked_devices
          ? `Password updated. Signed out ${r.data.revoked_devices} other device(s).`
          : "Password updated."
      );
      setCurrentPassword(""); setNewPassword(""); setConfirmPassword(""); setSignOutAll(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update password");
    } finally {
      setBusy(false);
    }
  };

  const inputCls = "mt-1 w-full bg-transparent border rounded-md px-3 py-2 outline-none focus:border-[var(--gf-accent)]";
  const inputStyle = { borderColor: "var(--gf-border-strong)", color: "var(--gf-text)" };

  return (
    <div className="App" data-testid="account-security-page">
      <Header hideSearch />
      <main className="max-w-xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
        <div className="text-xs uppercase tracking-[0.25em]" style={{ color: "var(--gf-text-muted)" }}>Account · Security</div>
        <h1 className="font-display text-3xl sm:text-4xl mt-2" style={{ color: "var(--gf-text)" }}>Change password</h1>
        <p className="mt-3 text-sm" style={{ color: "var(--gf-text-soft)" }}>
          For your security, we require your current password. If your device isn't recognized, we'll ask for extra verification when you sign in next.
        </p>

        <form onSubmit={submit} className="mt-8 space-y-4" data-testid="change-password-form">
          <div>
            <label htmlFor="acc-sec-current-password" className="text-xs uppercase tracking-[0.2em]" style={{ color: "var(--gf-text-muted)" }}>Current password</label>
            <PasswordInput
              id="acc-sec-current-password"
              data-testid="change-pw-current"
              required
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              className={inputCls}
              style={inputStyle}
              autoComplete="current-password"
            />
          </div>
          <div>
            <label htmlFor="acc-sec-new-password" className="text-xs uppercase tracking-[0.2em]" style={{ color: "var(--gf-text-muted)" }}>New password</label>
            <PasswordInput
              id="acc-sec-new-password"
              data-testid="change-pw-new"
              required
              minLength={8}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className={inputCls}
              style={inputStyle}
              autoComplete="new-password"
            />
            <p className="text-[11px] mt-1" style={{ color: "var(--gf-text-muted)" }}>
              At least 8 characters with uppercase, lowercase, a number, and a special character.
            </p>
          </div>
          <div>
            <label htmlFor="acc-sec-confirm-password" className="text-xs uppercase tracking-[0.2em]" style={{ color: "var(--gf-text-muted)" }}>Confirm new password</label>
            <PasswordInput
              id="acc-sec-confirm-password"
              data-testid="change-pw-confirm"
              required
              minLength={8}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className={inputCls}
              style={inputStyle}
              autoComplete="new-password"
            />
          </div>
          <label className="flex items-center gap-2 text-sm mt-2 select-none" data-testid="change-pw-signout-all-wrap">
            <input
              data-testid="change-pw-signout-all"
              type="checkbox"
              checked={signOutAll}
              onChange={(e) => setSignOutAll(e.target.checked)}
            />
            <span style={{ color: "var(--gf-text-soft)" }}>Sign out of all other trusted devices</span>
          </label>
          <button
            data-testid="change-pw-submit"
            type="submit"
            disabled={busy}
            className="w-full rounded-md font-medium py-2.5 disabled:opacity-50 mt-2"
            style={{ background: "var(--gf-accent)", color: "var(--gf-on-accent)" }}
          >
            {busy ? "Updating…" : "Update password"}
          </button>
        </form>

        <div className="mt-10 rounded-2xl border p-5" style={{ borderColor: "var(--gf-border)", background: "var(--gf-surface)" }}>
          <div className="font-display text-lg" style={{ color: "var(--gf-text)" }}>Never share your password</div>
          <p className="text-xs mt-1" style={{ color: "var(--gf-text-muted)" }}>
            BookFreely will never ask for your password by email or phone. If someone does, forward the message to <a className="underline" href="mailto:bookfreely.support@gmail.com">bookfreely.support@gmail.com</a>.
          </p>
        </div>
      </main>
      <Footer />
    </div>
  );
}
